import { z } from 'zod';
import { monetizationStatusSchema, PERIODS, periodSchema } from './channels.js';
import { moduleIdSchema } from './core.js';
import { granularitySchema } from './sync.js';
import { maKySchema } from './ky.js';
/* ------------------------------------------------------------------ */
/* Truy vấn                                                            */
/* ------------------------------------------------------------------ */
/**
 * Kỳ của trang Tổng quan — **đúng bằng** `PERIODS` của module 01.
 *
 * Trước đây chỉ có `7d|28d|90d`. Ô chọn kỳ chung ở thanh trên dùng một danh
 * sách cho cả app (xem `layout/kyChung`), nên hai nơi lệch nhau nghĩa là bấm
 * "365 ngày" ở trang Kênh rồi sang Tổng quan là lỗi 400. Số ngày tra ở
 * `PERIOD_DAYS`; `all` không có kỳ trước để so (xem `coKyTruoc`).
 */
export const DASHBOARD_RANGES = PERIODS;
export const dashboardRangeSchema = periodSchema;
export const dashboardQuerySchema = z
    .object({
    /**
     * **Mã kỳ** của cả app (`@qlk/shared/ky.ts`). Năm preset cũ vẫn hợp lệ,
     * nên link `?range=28d` không hỏng; thêm `YYYY`, `YYYY-MM`, `tu-…_den-…`.
     */
    range: maKySchema.default('28d'),
    /** Bí danh của `range` — tên tham số dùng chung với mọi endpoint khác. */
    ky: maKySchema.optional(),
})
    .transform((q) => ({ range: q.ky ?? q.range }));
/** Cache của trang tổng quan, tính bằng giây. */
export const DASHBOARD_CACHE_SECONDS = 300;
/* ------------------------------------------------------------------ */
/* Tổng hợp                                                            */
/* ------------------------------------------------------------------ */
/**
 * Mỗi khối gọi một module độc lập. Module lỗi thì khối đó bằng `null`,
 * phần còn lại của trang vẫn hiển thị.
 */
export const dashboardSummarySchema = z.object({
    range: z.string(),
    /** Nhãn kỳ để in trên nhãn ô thống kê: `28 ngày qua`, `Tháng 9/2026`. */
    nhanKy: z.string(),
    /**
     * Khoảng ngày **thực tế** của kỳ, kết thúc ở ngày gần nhất đã có số liệu
     * (YouTube Analytics trễ 1–3 ngày). Giao diện ghi dưới bộ chọn kỳ.
     */
    from: z.string(),
    to: z.string(),
    channels: z
        .object({
        total: z.number().int().nonnegative(),
        monetized: z.number().int().nonnegative(),
        viaNetwork: z.number().int().nonnegative(),
    })
        .nullable(),
    audience: z
        .object({
        subscribers: z.number().int().nonnegative(),
        /**
         * Tăng trưởng subscribers trong kỳ, so với số đầu kỳ. `null` khi không đủ
         * dữ liệu (kênh mới, hoặc đầu kỳ bằng 0).
         */
        subscribersDeltaPercent: z.number().nullable(),
        /**
         * Lượt xem của **những ngày có số liệu** trong kỳ.
         *
         * `null` = chưa có ngày nào có số liệu lượt xem; giao diện hiện `—`, không
         * hiện 0. Cùng nguồn với cột Xem của `/kenh` và tab Hiệu suất:
         * `channel_daily_analytics.views`, chỉ khác khoảng ngày của từng kỳ.
         */
        views: z.number().int().nonnegative().nullable(),
        /// Lượt xem trong kỳ so với kỳ trước liền kề. `null` khi kỳ trước không có dữ liệu.
        viewsDeltaPercent: z.number().nullable(),
        /// Số ngày trong kỳ thật sự có lượt xem, để giao diện ghi "N/28 ngày".
        soNgayCoViews: z.number().int().nonnegative(),
        /// Số ngày của kỳ.
        soNgayTrongKy: z.number().int().nonnegative(),
        watchHours: z.number(),
        /// Lượt xem 12 tuần gần nhất, dùng cho biểu đồ đường.
        /**
         * Chuỗi lượt xem của **đúng kỳ đang xem**, độ chi tiết tự chọn theo độ
         * dài kỳ (`doChiTietCuaKy`): ≤ 90 ngày vẽ theo ngày, ≤ 2 năm theo tuần,
         * dài hơn theo tháng.
         *
         * Trước 23/10/2026 khối này luôn là **12 tuần cố định** bất kể ô chọn kỳ
         * — chọn "7 ngày qua" mà biểu đồ vẫn vẽ ba tháng.
         */
        chuoi: z.array(z.object({
            /** `2026-10-20` (ngày), `2026-W43` (tuần) hay `2026-10` (tháng). */
            moc: z.string(),
            views: z.number().int().nonnegative(),
            /** Mốc cuối chưa trọn (tuần/tháng đang chạy) — giao diện vẽ nét đứt. */
            chuaTron: z.boolean(),
        })),
        doChiTiet: granularitySchema,
    })
        .nullable(),
    /**
     * Thẻ doanh thu của kỳ. `null` = module 08 không trả được số.
     *
     * **Bỏ hẳn khoá** khi người xem không có `revenue:view` (xem `loc-tien.ts`):
     * `null` nghĩa "không lấy được số", `undefined` nghĩa "bạn không được xem" —
     * giao diện phải phân biệt được hai câu đó.
     */
    revenue: z
        .object({
        gross: z.string(),
        partnerShare: z.string(),
        companyNet: z.string(),
        currency: z.string(),
        deltaPercent: z.number().nullable(),
    })
        .nullish(),
    networks: z
        .object({
        partners: z.number().int().nonnegative(),
        activeContracts: z.number().int().nonnegative(),
    })
        .nullable(),
    music: z
        .object({
        ownTracks: z.number().int().nonnegative(),
        partnerTracks: z.number().int().nonnegative(),
        expiringSoon: z.number().int().nonnegative(),
    })
        .nullable(),
    production: z
        .object({
        byStage: z.record(z.string(), z.number().int().nonnegative()),
        /// Tổng số bài nằm trên lịch của tuần hiện tại, mọi trạng thái.
        thisWeek: z.number().int().nonnegative(),
        /**
         * Tách theo `ScheduledPostStatus`. "4 video trong lịch tuần này" gộp cả bài
         * đã đăng lẫn bài còn chờ duyệt, nên giao diện phải nói rõ từng loại.
         */
        thisWeekByStatus: z.record(z.string(), z.number().int().nonnegative()),
    })
        .nullable(),
    /**
     * Kênh nổi bật **theo doanh thu** trong kỳ.
     *
     * **Bỏ hản** khi thiếu `revenue:view` (mục S5): không chỉ vì hai cột
     * `companyNet`/`rpm`, mà vì **thứ tự** của danh sách cũng là một con số
     * tiền — cùng lý do với `topThucNhan` và `revenueShare`.
     */
    topChannels: z
        .array(z.object({
        channelId: z.string(),
        name: z.string(),
        avatarColor: z.string(),
        /// `null` = chưa có số liệu lượt xem cho kỳ này.
        views: z.number().int().nonnegative().nullable(),
        companyNet: z.string(),
        /// `null` = chưa đủ số liệu lượt xem (xem `sync.mauSoRpm`).
        rpm: z.string().nullable(),
    }))
        .nullish(),
});
/* ------------------------------------------------------------------ */
/* Cảnh báo                                                            */
/* ------------------------------------------------------------------ */
export const ALERT_TONES = ['bad', 'warn', 'info'];
export const alertToneSchema = z.enum(ALERT_TONES);
/** Thứ tự sắp xếp: lỗi trước, rồi cảnh báo, rồi thông tin. */
export const ALERT_TONE_RANK = { bad: 0, warn: 1, info: 2 };
export const dashboardAlertSchema = z.object({
    id: z.string(),
    /// Module sinh ra cảnh báo.
    module: moduleIdSchema,
    tone: alertToneSchema,
    title: z.string(),
    text: z.string(),
    /// Đường dẫn trong app để xử lý cảnh báo.
    link: z.string(),
    createdAt: z.iso.datetime().nullable(),
});
export const dashboardAlertsSchema = z.object({
    items: z.array(dashboardAlertSchema),
    /// Module nào không trả được cảnh báo (lỗi hoặc chưa cài).
    unavailable: z.array(moduleIdSchema),
});
/* ------------------------------------------------------------------ */
/* Phần thêm cho giao diện bước 2/3                                    */
/* ------------------------------------------------------------------ */
/** Đường tiền của một kênh, dùng cho chip và cho thanh xếp chồng. */
export const NGUON_TIEN = ['network', 'adsense', 'chua-kiem-tien'];
export const NGUON_TIEN_LABEL = {
    network: 'Qua Network',
    adsense: 'AdSense trực tiếp',
    'chua-kiem-tien': 'Chưa kiếm tiền',
};
/**
 * Số liệu **gộp thêm** cho trang Tổng quan: những thứ `/summary` không có mà
 * màn hình mới cần, gom vào **một** lượt gọi.
 *
 * Tách endpoint riêng thay vì nhồi vào `DashboardSummary`: hợp đồng của
 * `/summary` đã chốt ở 01b và còn `GET /xuat-bao-cao` đọc cùng kiểu dữ liệu.
 */
export const dashboardExtraSchema = z.object({
    range: z.string(),
    /** Lần đồng bộ YouTube gần nhất (module 03); `null` khi chưa chạy lần nào. */
    lastSyncAt: z.iso.datetime().nullable(),
    /**
     * Đếm kênh đang hoạt động theo tình trạng kiếm tiền. Dải chỉ số ghi
     * "10/12" kèm "1 bị từ chối · 1 chưa đủ ĐK" nên cần từng trạng thái, không
     * chỉ tổng số kênh kiếm tiền.
     */
    theoKiemTien: z.record(monetizationStatusSchema, z.number().int().nonnegative()),
    /**
     * Đếm kênh theo đường tiền. Tổng ba số = số kênh đang hoạt động.
     *
     * **Bỏ hẳn** khi thiếu `revenue:view` — khối "Nguồn tiền" của trang Tổng
     * quan là một khối tiền.
     */
    theoNguonTien: z
        .object({
        network: z.number().int().nonnegative(),
        adsense: z.number().int().nonnegative(),
        'chua-kiem-tien': z.number().int().nonnegative(),
    })
        .optional(),
    /**
     * Năm kênh thực nhận cao nhất trong kỳ, đã sắp từ cao xuống thấp.
     *
     * **Bỏ hẳn** khi thiếu `revenue:view`: không chỉ vì các cột tiền, mà vì
     * **thứ tự** của năm dòng cũng là một con số tiền.
     */
    topThucNhan: z
        .array(z.object({
        channelId: z.string(),
        name: z.string(),
        avatarColor: z.string(),
        nguonTien: z.enum(NGUON_TIEN),
        /** Tên Network đang trả tiền; `null` với AdSense trực tiếp hoặc chưa gán. */
        partnerName: z.string().nullable(),
        companyNet: z.string(),
        currency: z.string(),
        /** Tỷ trọng thực nhận của kênh trên tổng thực nhận của kỳ, 0–100. */
        percent: z.number(),
        /* --- Chia % nhân sự (module 10 qua enricher `chiaSe`) --- */
        /** Tổng chia của kênh. **Chỉ người xem đủ** — xem `chiaNhanSu` bên dưới. */
        chiaNhanSu: z.number().optional(),
        /** Lợi nhuận − tổng chia. **Chỉ người xem đủ.** */
        congTyGiuLai: z.number().optional(),
        /** Phần của **người đang xem**; `null` = không hưởng % ⇒ hiện `—`. */
        phanCuaToi: z.number().nullable(),
        /** Tỷ lệ của người đang xem; `null` khi đổi giữa kỳ hoặc khoán cố định. */
        pctCuaToi: z.number().nullable(),
    }))
        .optional(),
    /**
     * Chia % nhân sự của **mọi kênh đang hoạt động** trong kỳ — hai ô "Chia nhân
     * sự" / "Công ty giữ lại" (người xem đủ) hoặc ô "Thu nhập của bạn" (còn lại).
     *
     * `null` khi module 10 không trả được dữ liệu; dải chỉ số khi đó hiện `—` kèm
     * lý do, không hiện 0.
     *
     * ⚠️ Khối này **phụ thuộc người đang xem**, nên khoá cache của `/them` phải
     * chứa `userId` và cờ xem đủ — xem `dashboard/routes.ts`.
     */
    /**
     * Chuỗi tiền của **mọi kênh trong phạm vi**, từ doanh thu gộp tới thực nhận:
     * `gộp − thuế Mỹ − phần Network − VAT = thực nhận`.
     *
     * Khối "Dòng tiền" của trang Tổng quan vẽ một thanh xếp chồng mà **tổng các
     * đoạn phải bằng gộp**, nên nó cần cả hai khoản thuế chứ không chỉ phần
     * Network. Số lấy từ enricher `revenueSummary` — tức đúng
     * `revenue.tinhThucNhanKenh`, cùng hàm mà tab Hiệu suất và trang Doanh thu
     * dùng — rồi cộng qua cent (`tongUsd`).
     *
     * Người bị giới hạn kênh chỉ nhận tổng của **kênh được giao**: enricher chạy
     * trên đúng tập kênh mà `listChannels` trả về.
     */
    chuoiTien: z
        .object({
        gross: z.number(),
        /** Thuế Google giữ trên phần doanh thu phát sinh ở Mỹ; 0 khi kênh khai 0%. */
        thueMy: z.number(),
        phanNetwork: z.number(),
        vat: z.number(),
        /** Sau cả ba khoản trên — **cùng số** với ô "Công ty thực nhận". */
        thucNhan: z.number(),
        currency: z.string(),
    })
        .optional(),
    chiaNhanSu: z
        .object({
        /**
         * Thực nhận của mọi kênh có số chia — **cùng nguồn** với
         * `chuoiTien.thucNhan` (enricher `chiaSe` nhận chính `companyNet` của
         * `revenueSummary`), nên thanh xếp chồng không hụt một đoạn ở giữa.
         */
        thucNhan: z.number(),
        chiPhiVanHanh: z.number(),
        loiNhuan: z.number(),
        /**
         * `true` khi còn kênh nào trong kỳ **chưa chốt** ở trang Đối soát nhân
         * sự ⇒ giao diện gắn chip "tạm tính". Tháng đã chốt đủ thì bỏ chip: số đó
         * là số đã đem đi trả tiền (`docs/ui.md` mục 16).
         */
        tamTinh: z.boolean(),
        /** **Chỉ người xem đủ.** */
        chiaNhanSu: z.number().optional(),
        /** **Chỉ người xem đủ.** */
        congTyGiuLai: z.number().optional(),
        /** Tổng phần của người đang xem — số của chính họ, ai cũng được thấy. */
        phanCuaToi: z.number(),
        xemDayDu: z.boolean(),
    })
        .nullish(),
});
/* ------------------------------------------------------------------ */
/* Tăng trưởng lượt xem 7 ngày                                         */
/* ------------------------------------------------------------------ */
/**
 * Ngưỡng lượt xem **của tuần trước** để một kênh được xếp hạng tăng trưởng.
 *
 * Phần trăm tính trên một mẫu số nhỏ là một con số đúng công thức mà vô nghĩa:
 * kênh đi từ 40 lên 400 lượt xem ra `+900%` và chiếm hết năm dòng của khung,
 * đẩy kênh thật sự tăng 1,2 triệu lượt xuống dưới. Lọc theo **tuần trước** chứ
 * không theo 7 ngày qua: lọc theo tuần này thì một kênh tụt từ 2 triệu về 500
 * lượt vẫn bị loại khỏi "Giảm mạnh nhất" — đúng cái cần thấy nhất.
 */
export const NGUONG_TANG_TRUONG_7_NGAY = 10_000;
/** Số kênh của khung tăng trưởng. */
export const SO_DONG_TANG_TRUONG = 5;
/** Số ngày của mỗi nửa chuỗi: 7 ngày qua và 7 ngày liền trước. */
export const SO_NGAY_NUA_TANG_TRUONG = 7;
export const HUONG_TANG_TRUONG = ['tang', 'giam'];
export const huongTangTruongSchema = z.enum(HUONG_TANG_TRUONG);
export const THEO_TANG_TRUONG = ['pct', 'luot'];
export const theoTangTruongSchema = z.enum(THEO_TANG_TRUONG);
export const tangTruong7NgayQuerySchema = z.object({
    huong: huongTangTruongSchema.default('tang'),
    theo: theoTangTruongSchema.default('pct'),
});
export const dongTangTruongSchema = z.object({
    channelId: z.string(),
    ten: z.string(),
    /** Tên Network đang trả tiền; `null` với AdSense trực tiếp hoặc chưa gán. */
    network: z.string().nullable(),
    /** Tổng lượt xem 7 ngày qua. */
    luot7: z.number().int().nonnegative(),
    /** Tổng lượt xem của 7 ngày liền trước đó. */
    luotTruoc: z.number().int().nonnegative(),
    /** `luot7 − luotTruoc`, có dấu. */
    delta: z.number().int(),
    /** `delta ÷ luotTruoc` theo phần trăm, một chữ số thập phân. */
    pct: z.number(),
    /**
     * 14 giá trị lượt xem theo ngày, từ cũ tới mới: 7 ngày của tuần trước rồi 7
     * ngày qua. Luôn đủ 14 phần tử — kênh thiếu một ngày thì **không** được xếp
     * hạng, nên không có chỗ nào phải vẽ một lỗ hổng.
     */
    chuoi14: z.array(z.number().int().nonnegative()),
});
/**
 * Khung "Tăng trưởng lượt xem · 7 ngày" của trang Tổng quan.
 *
 * Khung này **luôn** tính 7 ngày, không đổi theo ô chọn kỳ của trang: câu hỏi
 * của nó là "tuần này kênh nào đổi nhiều nhất", và câu đó chỉ có một kỳ.
 */
export const tangTruong7NgaySchema = z.object({
    /** Ngày đầu của 7 ngày qua (`YYYY-MM-DD`). */
    tu: z.string(),
    /** Ngày cuối — ngày gần nhất **đã có số liệu**, cùng mốc với các ô khác. */
    den: z.string(),
    items: z.array(dongTangTruongSchema),
});
//# sourceMappingURL=dashboard.js.map